import { makeApi } from "@zodios/core";
import { z } from "zod";

import { GenerationErrorResponse } from "../response.ts";

export const Refinement = z.enum([
  "increase-length",
  "decrease-length",
  "change-tone",
  "change-audience",
  "change-tense",
  "change-perspective",
  "improve-accuracy",
  "improve-flow",
  "improve-clarity",
  "simplify-language",
  "elaborate",
  "write-question",
  "translate",
  "proofread-and-fix",
  "match-style",
  "placeholder-fill",
  "rephrase",
  "convert-to-list",
]);

export type Refinement = z.infer<typeof Refinement>;

export const RefinementOptions = z.object({
  "increase-length": z.enum(["25%", "33%", "50%", "75%", "100%", "125%", "150%", "200%", "300%"]),
  "decrease-length": z.enum(["25%", "33%", "50%", "75%", "100%", "125%", "150%", "200%", "300%"]),
  "change-tone": z.enum([
    "Serious",
    "Humorous",
    "Happy",
    "Sad",
    "Angry",
    "Optimistic",
    "Pessimistic",
    "Empathetic",
    "Formal",
    "Informal",
    "Academic",
    "Didactic",
    "Reflective",
    "Persuasive",
    "Cautionary",
    "Neutral",
    "Ironic",
    "Inspiring",
  ]),
  "change-audience": z.enum([
    "Academic",
    "Professional",
    "Learners",
    "General Public",
    "Consumers",
    "Children",
    "Technical",
  ]),
  "change-tense": z.enum(["Past", "Present", "Future"]),
  "change-perspective": z.enum(["1st Person", "2nd Person", "3rd Person", "4th Person"]),
  "improve-accuracy": z.enum([""]),
  "improve-flow": z.enum([""]),
  "improve-clarity": z.enum([""]),
  "simplify-language": z.enum([""]),
  elaborate: z.enum([""]),
  "write-question": z.enum([""]),
  translate: z.enum([""]),
  "proofread-and-fix": z.enum([""]),
  "match-style": z.enum(["Explanatory", "Narrative", "Persuasive", "Descriptive", "Technical", "Analytic"]),
  "placeholder-fill": z.enum([""]),
  rephrase: z.enum([""]),
  "convert-to-list": z.enum([""]),
});

export const ContentToRefine = z.object({
  prompt: z.string().describe("The prompt to be used"),
  content: z.string().describe("The content to be refined"),
  refinements: z.record(Refinement, z.string()).optional().describe("The refinements to be applied"),
});

export type ContentToRefine = z.infer<typeof ContentToRefine>;

export const RefinedContent = z.object({
  content: z.string().default("").describe("The refined content"),
});

export type RefinedContent = z.infer<typeof RefinedContent>;

export const refinedContentApi = makeApi([
  {
    method: "get",
    path: "/copilot/refined-content",
    alias: "getRefinedContent",
    parameters: [
      {
        type: "Query",
        name: "input",
        schema: ContentToRefine,
      },
    ],
    response: RefinedContent,
    errors: [
      { status: 400, schema: GenerationErrorResponse },
      { status: 401, schema: z.string() },
    ],
  },
]);
